import { inject, Injectable } from '@angular/core';
import {
  clearChatReplyQuoteData,
  cloneChatReplyMessageSnapshot,
  createChatReplyMessageSnapshot,
} from '@chat/utils/chat-reply-snapshot.utils';
import { MessageVm } from '@chat/interfaces/chat.interfaces';
import type { MessageData } from '@chat/interfaces/chat-message-data.interfaces';
import { ChatOutboxMessage } from '@services/chat-outbox.interfaces';
import { AuthService } from '@services/auth.service';
import { LocalStorageService } from '@services/local-storage.service';

@Injectable({
  providedIn: 'root'
})
export class ChatOutboxService {
  private readonly authService = inject(AuthService);
  private readonly localStorage = inject(LocalStorageService);
  private readonly storageKeyPrefix = 'sendingMessages-';

  public getMessagesForChat(chatId: number | null | undefined): ChatOutboxMessage[] {
    const normalizedChatId = this.normalizeChatId(chatId);
    if (normalizedChatId === null) {
      return [];
    }

    try {
      return this.readAll()
        .filter((message: ChatOutboxMessage): boolean => message.chat_id === normalizedChatId);
    } catch (error: unknown) {
      console.error('Unable to restore chat outbox.', {chatId: normalizedChatId, error});
      return [];
    }
  }

  public replaceMessagesForChat(chatId: number | null | undefined, messages: ChatOutboxMessage[]): void {
    const normalizedChatId = this.normalizeChatId(chatId);
    if (normalizedChatId === null) {
      return;
    }

    const otherMessages = this.readAll().filter((message: ChatOutboxMessage): boolean => message?.chat_id !== normalizedChatId);
    const nextChatMessages = this.normalizeMessages(messages)
      .filter((message: ChatOutboxMessage): boolean => message?.chat_id === normalizedChatId);

    this.writeAll([...otherMessages, ...nextChatMessages]);
  }

  public upsert(message: ChatOutboxMessage): void {
    if (!message) return;

    const messages = this.readAll();
    const normalizedMessage = this.normalizeMessage(message);
    if (!normalizedMessage) return;

    const guid = typeof normalizedMessage.guid === 'string' ? normalizedMessage.guid : '';
    if (!guid) {
      messages.push(normalizedMessage);
      this.writeAll(messages);
      return;
    }

    const index = messages.findIndex(item => item?.guid === guid);
    if (index >= 0) {
      messages[index] = normalizedMessage;
    } else {
      messages.push(normalizedMessage);
    }
    this.writeAll(messages);
  }

  public removeByGuid(guid: string | null | undefined): void {
    if (!guid) return;
    const messages = this.readAll().filter(message => message?.guid !== guid);
    this.writeAll(messages);
  }

  public hasGuid(guid: string | null | undefined): boolean {
    if (!guid) return false;
    return this.readAll().some(message => message?.guid === guid);
  }

  public findByGuid(guid: string | null | undefined): ChatOutboxMessage | null {
    if (!guid) return null;
    return this.readAll().find(message => message?.guid === guid) || null;
  }

  public clearReplyTargetByMessageId(chatId: number | null | undefined, messageId: string | null | undefined): void {
    const normalizedChatId = this.normalizeChatId(chatId);
    const normalizedMessageId = this.normalizeMessageKey(messageId);
    if (normalizedChatId === null || !normalizedMessageId) return;

    let changed = false;
    const messages = this.readAll().map((message: ChatOutboxMessage): ChatOutboxMessage => {
      if (message?.chat_id !== normalizedChatId || !this.messageRepliesTo(message, normalizedMessageId)) {
        return message;
      }

      changed = true;
      return {
        ...message,
        reply_message_id: null,
        replyMessageSnapshot: null,
        data: clearChatReplyQuoteData(message.data),
      };
    });

    if (changed) {
      this.writeAll(messages);
    }
  }

  public updateReplyMessageSnapshot(guid: string | null | undefined, replyMessage: MessageVm | null | undefined): void {
    if (!guid) return;

    const messages = this.readAll();
    const index = messages.findIndex(message => message?.guid === guid);
    if (index < 0) return;

    const replyMessageSnapshot = createChatReplyMessageSnapshot(replyMessage);
    messages[index].replyMessageSnapshot = replyMessageSnapshot;
    if (replyMessageSnapshot?.messageId) {
      messages[index].reply_message_id = replyMessageSnapshot.messageId;
    }
    this.writeAll(messages);
  }

  private messageRepliesTo(message: ChatOutboxMessage | null | undefined, messageId: string): boolean {
    return this.normalizeMessageKey(message?.reply_message_id) === messageId
      || this.normalizeMessageKey(message?.replyMessageSnapshot?.messageId) === messageId;
  }

  private readAll(): ChatOutboxMessage[] {
    const storageKey = this.getStorageKey();
    if (!storageKey) {
      return [];
    }

    let raw: unknown;
    try {
      raw = this.localStorage.getItem(storageKey, true);
    } catch (error: unknown) {
      if (!(error instanceof SyntaxError)) {
        throw error;
      }

      console.error('Invalid JSON in chat outbox; removing corrupted entry.', {storageKey, error});
      this.removeInvalidStoredOutbox(storageKey);
      return [];
    }

    const normalized = this.normalizeMessages(raw);
    if (Array.isArray(raw) && raw.length !== normalized.length) {
      this.persistNormalizedOutbox(normalized);
    } else if (raw !== null && !Array.isArray(raw)) {
      this.removeInvalidStoredOutbox(storageKey);
    }
    return normalized;
  }

  private writeAll(messages: ChatOutboxMessage[]): void {
    const storageKey = this.getStorageKey();
    if (!storageKey) {
      return;
    }

    this.localStorage.setItem(storageKey, this.normalizeMessages(messages));
  }

  private normalizeMessages(raw: unknown): ChatOutboxMessage[] {
    const items = Array.isArray(raw) ? raw : [];
    const result: ChatOutboxMessage[] = [];
    const byGuid = new Map<string, number>();

    items.forEach((item: unknown) => {
      const normalizedItem = this.normalizeMessage(item);
      if (!normalizedItem) return;

      const existingIndex = byGuid.get(normalizedItem.guid);
      if (typeof existingIndex === 'number') {
        result[existingIndex] = normalizedItem;
      } else {
        byGuid.set(normalizedItem.guid, result.length);
        result.push(normalizedItem);
      }
    });

    return result;
  }

  private normalizeMessage(raw: unknown): ChatOutboxMessage | null {
    if (!this.isRecord(raw)) {
      return null;
    }

    const chatId = this.normalizeChatId(Reflect.get(raw, 'chat_id'));
    const message = Reflect.get(raw, 'message');
    const guid = this.normalizeMessageKey(Reflect.get(raw, 'guid'));
    const fileIds = this.normalizeFileIds(Reflect.get(raw, 'file_ids'));
    const rawToId = Reflect.get(raw, 'to_id');
    const toId = this.normalizeUserId(rawToId);

    if (chatId === null
      || typeof message !== 'string'
      || !guid
      || fileIds === null
      || (rawToId !== null && rawToId !== undefined && toId === null)) {
      return null;
    }

    return {
      chat_id: chatId,
      to_id: rawToId === undefined ? undefined : toId,
      message,
      file_ids: fileIds,
      guid,
      reply_message_id: this.normalizeMessageKey(Reflect.get(raw, 'reply_message_id')),
      data: this.normalizeMessageData(Reflect.get(raw, 'data')),
      replyMessageSnapshot: cloneChatReplyMessageSnapshot(Reflect.get(raw, 'replyMessageSnapshot')),
    };
  }

  private normalizeMessageData(value: unknown): MessageData | null {
    return this.isRecord(value) ? {...value} : null;
  }

  private normalizeFileIds(value: unknown): number[] | null {
    if (!Array.isArray(value)) {
      return null;
    }

    const normalized = value.filter((fileId: unknown): fileId is number => {
      return typeof fileId === 'number' && Number.isInteger(fileId) && fileId > 0;
    });
    return normalized.length === value.length ? normalized : null;
  }

  private normalizeChatId(chatId: unknown): number | null {
    if (typeof chatId !== 'number' || !Number.isInteger(chatId) || chatId < 1) {
      return null;
    }

    return chatId;
  }

  private normalizeMessageKey(value: unknown): string | null {
    if (value === null || value === undefined) return null;
    if (typeof value !== 'string' && typeof value !== 'number') return null;
    if (typeof value === 'number' && !Number.isFinite(value)) return null;
    const normalized = String(value).trim();
    return normalized.length > 0 ? normalized : null;
  }

  private normalizeUserId(userId: unknown): number | null {
    if (typeof userId !== 'number' || !Number.isInteger(userId) || userId < 1) {
      return null;
    }

    return userId;
  }

  private isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
  }

  private persistNormalizedOutbox(messages: ChatOutboxMessage[]): void {
    try {
      this.writeAll(messages);
    } catch (error: unknown) {
      console.error('Unable to persist normalized chat outbox.', {error});
    }
  }

  private removeInvalidStoredOutbox(storageKey: string): void {
    try {
      this.localStorage.removeItem(storageKey);
    } catch (error: unknown) {
      console.error('Unable to remove invalid chat outbox.', {storageKey, error});
    }
  }

  private getStorageKey(): string | null {
    const normalizedUserId = this.normalizeUserId(this.authService.parsedToken?.userId);
    if (normalizedUserId === null) {
      return null;
    }

    return `${this.storageKeyPrefix}${normalizedUserId}`;
  }
}
